// ICT-8 "Emailing the Completed Red Report" — at the end of the day the Red
// Report goes to the ICT manager, Discovery, Verifications and ict@, with
// column B (status) filled in from what was done on each job today. Pure:
// the caller passes jobs, the live receipts and the "as of" date.
import type { MaterialReceiptRow } from '../types/api';
import type { ProxyJob } from '../data/mockProxyJobs';
import { pullFollowUpReport } from './followUpReportPull';
import type { ResolvedProxyJob } from './proxyQueue';

export const RED_REPORT_TO = 'ict-manager@betanxt.com';
export const RED_REPORT_CC = ['documents@betanxt.com', 'verifications@betanxt.com', 'ict@betanxt.com'];
export const NOT_WORKED = 'Not worked yet';

export interface RedReportLine {
  job: ResolvedProxyJob;
  status: string; // column B
}

function statusFor(job: ResolvedProxyJob, today: string): string {
  const done = job.followUpHistory.filter(r => r.timestamp.startsWith(today));
  if (!done.length) return NOT_WORKED;
  if (done.some(r => r.type === 'filing_pulled')) return 'Filing pulled, emails released';
  if (done.some(r => r.type === 'vif_bre_only')) return 'VIF/BRE only';
  if (!done.some(r => r.type === 'material_followup_documented')) return 'Worked, see MIC';
  switch (job.decision.action) {
    case 'POD': return job.decision.outsideGuidelines ? 'POD, escalated to manager' : 'POD Request sent';
    case 'EMAIL_ONLY_CANCEL_FULLSETS': return 'Email only, full sets cancelled';
    case 'ABORT': return 'Aborted';
    case 'HOLD_MAIL_DATE': return `Mail Date: ${job.mailDate}`;
    case 'FOLLOW_UP': return 'Red f/u sent';
    default: return 'Documented in MIC';
  }
}

// Today's Red Report: the Red jobs still on the live report, plus the ones
// chased today — the chase took them off the live report, but they were
// worked off today's.
export function buildRedReport(jobs: ProxyJob[], receipts: MaterialReceiptRow[] | null, today: string): RedReportLine[] {
  const pull = pullFollowUpReport(jobs, receipts, today);
  const chasedToday = pull.excluded
    .filter(e => e.step === 'recent_follow_up' && e.job.band === 'Red' && e.job.followUpHistory.some(r => r.timestamp.startsWith(today)))
    .map(e => e.job);
  return [...pull.rows.filter(r => r.band === 'Red').map(r => r.job), ...chasedToday]
    .map(job => ({ job, status: statusFor(job, today) }));
}
